import { zodResolver } from "@hookform/resolvers/zod";
import { isLoading } from "apollo/utils";
import clsx from "clsx";
import {
  FILE_CATEGORIES,
  FILE_TABLE_ACTION_UPLOAD_DRAGNDROP,
} from "core/consts";
import { useIsDocsAndExtractActive } from "core/model/careseekers";
import { ONTOLOGIES } from "core/model/utils/ontologies";
import { useGetOntologiesForSelect } from "core/model/utils/ontologies/hooks";
import { preprocessToOptionalNumber } from "core/schemas/utils";
import {
  FileCategory,
  FileScope,
  FileShareMode,
  Filev2,
  QueryProgress,
  RecareFileType,
} from "core/types";
import {
  Button,
  ControlledInput,
  ControlledSelect,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "ds/ui";
import { SetAction } from "ds_legacy/components/Tables/GeneralTable/MenuActions";
import { FileFeatureData } from "dsl/ecosystems/PatientFiles/useFilesFeature";
import { useIsEligiblePage } from "dsl/ecosystems/PatientFiles/useUploadFile";
import { useIsScrollable } from "dsl/hooks";
import { EncryptedFileMessageTailwind } from "dsl/organisms/FileTables";
import { ArrowLeftIcon, LockIcon, UploadIcon, XIcon } from "lucide-react";
import { RefObject, useEffect, useRef } from "react";
import {
  FieldArrayWithId,
  FormProvider,
  Resolver,
  useFieldArray,
  useForm,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { useTranslations } from "translations";
import Translations from "translations/types";
import { z } from "zod";
import { ProcessedFile } from "..";
import { ExtractBanner, getFileExtension, getFileStem } from "./utils";

function useFileNameField(index: number) {
  const { control } = useFormContext<FormValues>();
  const stem = useWatch({
    control,
    defaultValue: "",
    name: `files.${index}.fileNameStem`,
  });
  const extension = useWatch({
    control,
    defaultValue: "",
    name: `files.${index}.fileNameExtension`,
  });

  return { extension: extension ?? "", stem: stem ?? "" };
}

export type FileInitialValues = {
  category?: { id: number; label: string; value: FileCategory } | null;
  fileName: string;
  kis_document_id_string: Filev2["kis_document_id_string"] | undefined;
  should_extract_on_upload: boolean;
} & ProcessedFile;

type FileSubmittedValues = {
  form_synced_with_file?: boolean;
  category_other?: string;
  category: FileCategory;
  fileName: string;
  kis_document_id_string: Filev2["kis_document_id_string"] | undefined;
  recare_file_type?: RecareFileType;
  share_mode?: FileShareMode;
  file_scope?: FileScope;
  should_extract_on_upload?: boolean;
};

export type UploadedFileResult = FileSubmittedValues & ProcessedFile;

export type FileUploadInitialValues = FileInitialValues;

export type FileUploadModalProps = {
  handleRemoveFile: (index: number) => void;
  onSubmit: (data: UploadedFileResult[]) => Promise<void>;
  initialValues: FileUploadInitialValues[];
  fileUploadProgress: QueryProgress;
  mutationProgress: QueryProgress;
  savedFormDataRef: React.MutableRefObject<{
    categories: (FileCategory | undefined)[];
    fileNames: (string | undefined)[];
  }>;
  setAction: SetAction<FileFeatureData>;
};

const fileUploadFormSchema = (translations: Translations) =>
  z.object({
    files: z.array(
      z
        .object({
          category: z.preprocess(
            (val) => {
              if (Array.isArray(val)) {
                const firstItem = val.length > 0 ? val[0] : undefined;
                return preprocessToOptionalNumber(firstItem);
              }
              return preprocessToOptionalNumber(val);
            },
            z
              .number({
                error: translations.fileUploader.newModal.errorRequiredCategory,
              })
              .refine((val): val is FileCategory =>
                Object.values(FILE_CATEGORIES).includes(val as FileCategory),
              ),
          ) as z.ZodType<FileCategory>,
          fileNameExtension: z.string(),
          fileNameStem: z
            .string()
            .refine(
              (val) => val.length > 0,
              translations.fileUploader.newModal.errorRequiredFileName,
            ),
        })
        .transform(({ fileNameExtension, fileNameStem, ...rest }) => ({
          ...rest,
          fileName: fileNameStem + fileNameExtension,
        })),
    ),
    should_extract_on_upload: z.boolean(),
  });

type FormValues = {
  files: {
    category?: FileCategory;
    fileNameExtension: string;
    fileNameStem: string;
    kis_document_id_string?: string;
  }[];
  should_extract_on_upload: boolean;
};

type FormValuesParsed = z.infer<ReturnType<typeof fileUploadFormSchema>>;

const FileOverviewRow = ({
  index,
  onRemoveFile,
}: {
  index: number;
  onRemoveFile: (index: number) => void;
}) => {
  const translations = useTranslations();
  const { extension } = useFileNameField(index);
  const fileCategoryOntologies = useGetOntologiesForSelect({
    sort: true,
    type: ONTOLOGIES.FILE_CATEGORY,
  });

  return (
    <div className="grid grid-cols-[1.5fr_1fr_auto] items-start gap-4">
      <div className="min-w-0">
        <ControlledInput
          data-testid={`files.${index}.fileNameStem`}
          endContent={
            <div className="flex items-center gap-2">
              {extension && (
                <span className="text-body-md whitespace-nowrap text-gray-600">
                  {extension}
                </span>
              )}
              <LockIcon className="text-gray-600" size={16} />
            </div>
          }
          isRequired
          label={translations.fileSection.fileUploadComponent.fileName}
          labelPlacement="outside-top"
          name={`files.${index}.fileNameStem`}
          placeholder={translations.fileSection.fileUploadComponent.fileName}
        />
      </div>
      <div className="min-w-0">
        <ControlledSelect
          isRequired
          items={fileCategoryOntologies}
          label={translations.fileSection.fileUploadComponent.fileCategory}
          name={`files.${index}.category`}
          placeholder={
            translations.fileSection.fileUploadComponent.fileCategory
          }
          renderItem={(item) => item.label}
          variant="outline"
        />
      </div>
      <div className="flex flex-col">
        <div aria-hidden className="h-6 shrink-0" />
        <div className="flex h-10 items-center">
          <Button
            aria-label={translations.actions.delete}
            color="black"
            id="file-upload-modal-remove-file"
            isIconOnly
            size="sm"
            variant="light"
            onPress={() => onRemoveFile(index)}
          >
            <XIcon className="text-gray-600" size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
};

const FileOverview = ({
  fields,
  modalBodyRef,
  mutationProgress,
  onRemoveFile,
}: {
  fields: FieldArrayWithId<FormValues, "files", "id">[];
  modalBodyRef: RefObject<HTMLDivElement | null>;
  mutationProgress: QueryProgress;
  onRemoveFile: (index: number) => void;
}) => {
  const translations = useTranslations();

  if (isLoading(mutationProgress)) {
    return (
      <>
        <h2>{translations.general.oneMoment}</h2>
        <p>{translations.fileSection.events.documentIsBeingUploaded}</p>
      </>
    );
  }

  return (
    <div ref={modalBodyRef} className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h2 className="text-heading-lg font-bold">
          {translations.extractDocs.fileUploadModal.instructionsCheckName}
        </h2>
        <EncryptedFileMessageTailwind />
      </div>
      {fields.map((field, index) => (
        <FileOverviewRow
          key={field.id}
          index={index}
          onRemoveFile={onRemoveFile}
        />
      ))}
    </div>
  );
};

export function FileUploadScreen({
  fileUploadProgress,
  handleRemoveFile,
  initialValues,
  mutationProgress,
  onSubmit,
  savedFormDataRef,
  setAction,
}: FileUploadModalProps) {
  const translations = useTranslations();
  const isEligiblePage = useIsEligiblePage();

  const { isDischargeLetterActive, isDocsAndExtractActive } =
    useIsDocsAndExtractActive();
  const { isScrollable, ref: modalBodyRef } = useIsScrollable<HTMLDivElement>();

  const showExtractCheckbox =
    (isDocsAndExtractActive || isDischargeLetterActive) && !isEligiblePage;

  const defaultValues: FormValues = {
    files: initialValues.map((file, index) => ({
      category:
        savedFormDataRef.current.categories[index] ?? file.category?.value,
      content: file.content,
      file: file.file,
      fileNameExtension: getFileExtension(file.fileName ?? ""),
      fileNameStem: getFileStem(file.fileName ?? ""),
      id: crypto.randomUUID(),
      kis_document_id_string: file.kis_document_id_string,
    })),
    should_extract_on_upload: showExtractCheckbox,
  };

  const methods = useForm<FormValues>({
    defaultValues,
    resolver: zodResolver(
      fileUploadFormSchema(translations),
    ) as unknown as Resolver<FormValues>,
  });

  const isRemovingRef = useRef(false);

  const { fields, remove, replace } = useFieldArray({
    control: methods.control,
    name: "files",
  });

  const shouldExtractOnUpload = useWatch({
    control: methods.control,
    name: "should_extract_on_upload",
  });

  const handleRemoveFileAtIndex = (index: number) => {
    isRemovingRef.current = true;

    const currentFiles = methods.getValues().files;
    savedFormDataRef.current = {
      categories: currentFiles
        .filter((_, i) => i !== index)
        .map((f) => f.category),
      fileNames: currentFiles
        .filter((_, i) => i !== index)
        .map((f) => f.fileNameStem + f.fileNameExtension),
    };

    remove(index);
    handleRemoveFile(index);

    // last file removed, go back to drag and drop screen
    if (fields.length === 1) {
      setAction({
        data: [] as FileFeatureData,
        type: FILE_TABLE_ACTION_UPLOAD_DRAGNDROP,
      });
    }
  };

  const handleFormSubmit = (data: FormValuesParsed) => {
    const filesToSubmit: UploadedFileResult[] = data.files.map(
      (fileForm, index) => {
        const initial = initialValues[index];
        if (!initial) throw new Error("Initial file data missing");
        if (!fileForm.category) throw new Error("Category is required");

        const file =
          fileForm.fileName !== initial.file.name
            ? new File([initial.file], fileForm.fileName, {
                type: initial.file.type,
              })
            : initial.file;

        return {
          ...initial,
          category: fileForm.category,
          file,
          fileName: fileForm.fileName,
          should_extract_on_upload: data.should_extract_on_upload,
        };
      },
    );

    onSubmit(filesToSubmit);
  };

  const initialValuesSignature =
    initialValues.length > 0
      ? `${initialValues.length}:${initialValues
          .map((f) => f.fileName)
          .sort((a, b) => a.localeCompare(b))
          .join(",")}`
      : "";

  useEffect(() => {
    if (isRemovingRef.current) {
      isRemovingRef.current = false;
      return;
    }
    if (!initialValues.length) return;
    const fullNames = savedFormDataRef.current.fileNames;
    const newFields = initialValues.map((file, index) => ({
      category:
        savedFormDataRef.current.categories[index] ?? file.category?.value,
      fileNameExtension: getFileExtension(
        fullNames[index] ?? file.fileName ?? "",
      ),
      fileNameStem: getFileStem(fullNames[index] ?? file.fileName ?? ""),
      kis_document_id_string: file.kis_document_id_string,
    }));

    replace(newFields);

    methods.reset({
      files: newFields,
      should_extract_on_upload: shouldExtractOnUpload,
    });
  }, [initialValuesSignature]);

  if (!initialValues.length) return null;

  return (
    <FormProvider {...methods}>
      <ModalContent>
        <ModalHeader
          as="h1"
          className="border-b border-divider pb-4 text-heading-xl! font-bold"
          id="file-upload-modal-drag-n-drop-title"
        >
          {translations.fileSection.dragAndDrop.title}
        </ModalHeader>
        <ModalBody className="p-6!">
          {isLoading(fileUploadProgress) ? (
            <div className="flex h-full items-center justify-center">
              <Spinner color="primary" size="lg" testId="file-upload-spinner" />
            </div>
          ) : (
            <FileOverview
              fields={fields}
              modalBodyRef={modalBodyRef}
              mutationProgress={mutationProgress}
              onRemoveFile={handleRemoveFileAtIndex}
            />
          )}
        </ModalBody>
        <ModalFooter
          className={clsx("flex w-full flex-col gap-4 p-6!", {
            "relative shadow-2xl!": isScrollable,
          })}
        >
          <ExtractBanner />
          <div className="flex flex-row justify-between gap-4">
            <Button
              color="primary"
              id="file-upload-modal-files-back"
              size="lg"
              startContent={<ArrowLeftIcon />}
              variant="ghost"
              onPress={() => {
                const currentFiles = methods.getValues().files;
                savedFormDataRef.current = {
                  categories: currentFiles.map((f) => f.category),
                  fileNames: currentFiles.map(
                    (f) => f.fileNameStem + f.fileNameExtension,
                  ),
                };
                setAction({
                  data: {} as FileFeatureData,
                  type: FILE_TABLE_ACTION_UPLOAD_DRAGNDROP,
                });
              }}
            >
              {translations.actions.back}
            </Button>
            <Button
              color="primary"
              id="file-upload-modal-files-upload"
              size="lg"
              startContent={<UploadIcon />}
              onPress={() => {
                methods.handleSubmit((data: unknown) =>
                  handleFormSubmit(data as FormValuesParsed),
                )();
              }}
            >
              {translations.actions.uploadFile}
            </Button>
          </div>
        </ModalFooter>
      </ModalContent>
    </FormProvider>
  );
}
